import { CostFeeType, CostScheduleGroup } from '@bariendo/lib/payables';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { fetchBotRead } from '@/services/botCalls';

export interface CostScheduleState {
  groups: CostScheduleGroup[];
  loading: boolean;
  error: string | undefined;
  reload: () => void;
}

/** One fee type's cost schedule, every version, partners named (BAR-3047). */
export function useCostSchedule(feeType: CostFeeType): CostScheduleState {
  const medplum = useMedplum();
  const [groups, setGroups] = useState<CostScheduleGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchBotRead<{ schedule: { groups: CostScheduleGroup[] } }>(medplum, 'create-treatment-payment', {
      operation: 'costSchedule',
      costFeeType: feeType,
    })
      .then(({ schedule }) => {
        if (!cancelled) {
          setGroups(schedule.groups);
          setError(undefined);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          // The last fee type's rates would read as this one's (BAR-3125).
          setGroups([]);
          setError(err instanceof Error ? err.message : 'Could not load the cost schedule');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, feeType, reloadKey]);

  return { groups, loading, error, reload };
}
